import { NATIVE_APP_CLIDS } from '@/constants/general'
import { ProviderKey } from '@/modules/adv/ad-service'
import { type getAdProvidersSettings } from '@/modules/adv/get-ad-providers-settings'
import { MINIPLAY_CLID } from '@/modules/main-frame/custom-integrations/miniplay'
import { type AdProvidersSettings, AdProviderTypes } from '@/types'
import type { Config } from '@/utils/clid-config'

export function getCurrentProvidersSettings({
    clid,
    providersSettings,
    clidConfig,
    isGameDistribution,
    isOppoWL,
    isExportOnWidget,
    isPartnerEmbed,
    isNativeAppEnv,
    isGoogleProvidersEnabled,
}: {
    clid: string
    providersSettings: ReturnType<typeof getAdProvidersSettings>
    clidConfig: Config
    isGameDistribution: boolean
    isOppoWL: boolean
    isExportOnWidget: boolean
    isPartnerEmbed: boolean
    isNativeAppEnv: boolean
    isGoogleProvidersEnabled: boolean
}): Partial<Record<ProviderKey, AdProvidersSettings[]>> {
    const NATIVE_APP_PROVIDERS = {
        [NATIVE_APP_CLIDS.directAndroidTest]: providersSettings.APP_NATIVE_ANDROID_TEST_PROVIDERS_SETTINGS,
        [NATIVE_APP_CLIDS.directAndroid]: providersSettings.APP_NATIVE_ANDROID_PROVIDERS_SETTINGS,
        [NATIVE_APP_CLIDS.directIosTest]: providersSettings.APP_NATIVE_IOS_TEST_PROVIDERS_SETTINGS,
        [NATIVE_APP_CLIDS.directIos]: providersSettings.APP_NATIVE_IOS_PROVIDERS_SETTINGS,
        [NATIVE_APP_CLIDS.directAndroidGameTest]: providersSettings.APP_NATIVE_ANDROID_TEST_PROVIDERS_SETTINGS,
        [NATIVE_APP_CLIDS.directAndroidGame]: providersSettings.APP_NATIVE_ANDROID_PROVIDERS_SETTINGS,
        [NATIVE_APP_CLIDS.directIosGameTest]: providersSettings.APP_NATIVE_IOS_TEST_PROVIDERS_SETTINGS,
        [NATIVE_APP_CLIDS.directIosGame]: providersSettings.APP_NATIVE_IOS_PROVIDERS_SETTINGS,
    }

    const CUSTOM_PROVIDERS_BY_CLID: Partial<Record<string, AdProvidersSettings[]>> = {
        [MINIPLAY_CLID]: providersSettings.MINIPLAY_PROVIDERS_SETTINGS,
    }

    const GOOGLE_PROVIDER_TYPES = new Set([
        AdProviderTypes.GAM_H5,
        AdProviderTypes.GAM_BANNER_INTERSTITIAL,
        AdProviderTypes.GAM_INSTREAM,
    ])

    function filterGoogleProviders(providers: AdProvidersSettings[]): AdProvidersSettings[] {
        return isGoogleProvidersEnabled
            ? providers
            : providers.filter((p) => !GOOGLE_PROVIDER_TYPES.has(p.type as AdProviderTypes))
    }

    if (isOppoWL) {
        if (clidConfig.adProvidersDisabled) {
            return {}
        }

        return {
            default: [providersSettings.GOOGLE_AD_SENSE_SETTINGS],
        }
    }

    if (isGameDistribution) {
        return {
            default: [providersSettings.GAME_DISTRIBUTION_PROVIDER_SETTINGS],
        }
    }

    let defaultProviders: AdProvidersSettings[] = []

    if (isNativeAppEnv) {
        defaultProviders = providersSettings.AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE
    } else {
        defaultProviders = providersSettings.AD_PROVIDER_SETTINGS_ON_PORTAL
    }

    let providers: AdProvidersSettings[] = []

    // custom
    if (CUSTOM_PROVIDERS_BY_CLID[clid] && isPartnerEmbed) {
        providers = CUSTOM_PROVIDERS_BY_CLID[clid]
    } else if (isNativeAppEnv && NATIVE_APP_PROVIDERS[clid]) {
        // for apps
        providers = NATIVE_APP_PROVIDERS[clid]
    } else if (isExportOnWidget) {
        // for isExportOnWidget / partners without custom providers
        providers = providersSettings.AD_PROVIDER_SETTINGS_FOR_GAME_PAGE_ON_WIDGET
    } else {
        providers = providersSettings.AD_PROVIDER_SETTINGS_FOR_GAME_PAGE
    }

    providers = filterGoogleProviders(providers)

    let fallbackProviders: AdProvidersSettings[] = []

    // custom fallback for partners
    if (isPartnerEmbed) {
        fallbackProviders = clidConfig.fallBackProviders ?? providersSettings.FALLBACK_AD_PROVIDER_SETTINGS_PARTNER
    } else if (isNativeAppEnv) {
        fallbackProviders = providersSettings.FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE
    } else {
        fallbackProviders = providersSettings.FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL
    }

    return {
        default: filterGoogleProviders(defaultProviders),
        interstitial: [...providers, ...fallbackProviders],
        interstitial_midroll: [...providers, ...fallbackProviders],
        interstitial_preroll: [...providers, ...fallbackProviders],
        rewarded: [...providers, ...fallbackProviders],
    }
}
